import { defineStore } from 'pinia';
import { ref } from 'vue';
import type { AxiosError } from 'axios';
import type {
  IHorraireCommerceBody,
  IHorraireCommerceResponse,
  INewScheduleItem,
  IWeekdayScheduleBody,
} from '@/types/horraire.commerce.types';
import { HorraireCommerceService } from '@/services/horraire.commerce.service';

/**
 * Store pour gérer les  currencies et leurs actions associées
 */
export const useHorraireCommerceStore = defineStore('HorraireCommerce', () => {
  /**
   * Crée une nouvelle devise via le service distant.
   *
   * Cette fonction envoie les données d'une devise au backend pour
   * l'enregistrement dans la base de données.
   *
   * @async
   * @function createHorraireCommerce
   * @param {IHorraireCommerceBody} data - Données de la devise à créer (label, name, description).
   * @returns {Promise<any>} Réponse de l'API contenant les informations de la devise créée.
   * @throws {AxiosError} Lève une erreur si la requête échoue ou si le service renvoie une erreur.
   */
  const createHorraireCommerce = async (
    data: IHorraireCommerceBody,
  ): Promise<any> => {
    try {
      const response =
        await HorraireCommerceService.createHorraireCommerce(data);
      return response;
    } catch (error) {
      return Promise.reject(error);
    }
  };

  /**
   * État réactif contenant la liste paginée des devises récupérées depuis l’API.
   *
   * Ce ref stocke la réponse typée de l’API après l’appel à `getHorraireCommerce`.
   * Il est mis à jour automatiquement lorsque les données sont chargées
   * depuis le service distant.
   *
   * @type {Ref<IHorraireCommerceResponse | null>}
   */
  const dataToGet = ref<IHorraireCommerceResponse | null>(null);

  /**
   * Récupère la liste des devises depuis le service distant.
   *
   * Cette fonction effectue une requête HTTP via `HorraireCommerceService`
   * pour récupérer la liste paginée des devises disponibles,
   * puis met à jour l’état réactif `dataToGet` avec les données renvoyées.
   *
   * @async
   * @function getHorraireCommerce
   * @returns {Promise<IHorraireCommerceResponse>} Promesse résolue contenant les données des devises récupérées.
   * @throws {AxiosError} Lève une erreur si la récupération échoue.
   */
  const getHorraireCommerce = async (): Promise<IHorraireCommerceResponse> => {
    try {
      const response = await HorraireCommerceService.getHorraireCommerce();
      dataToGet.value = response.data;
      return response.data;
    } catch (error) {
      return Promise.reject(error as AxiosError);
    }
  };

  /**
   * État réactif contenant les informations d’une devise récupérée depuis l’API.
   *
   * Ce `ref` stocke la réponse typée de l’API après l’appel à `getHorraireCommerceById`.
   * Il est mis à jour automatiquement lorsque les données sont chargées
   * depuis le service distant.
   *
   * @type {Ref<IHorraireCommerceDetailResponse | null>}
   */
  const dataToGetById = ref<any>(null);

  /**
   * Récupère une devise spécifique depuis le service distant par son identifiant (code).
   *
   * Cette fonction effectue une requête HTTP via `HorraireCommerceService`
   * pour obtenir les informations complètes de la devise, puis met à jour
   * l’état réactif `dataToGetById` avec les données reçues.
   *
   * @async
   * @function getHorraireCommerceById
   * @returns {Promise<IHorraireCommerceDetailResponse>} Promesse résolue contenant les données de la devise récupérée.
   * @throws {AxiosError} Lève une erreur si la récupération échoue.
   */
  const getHorraireCommerceById = async (): Promise<any> => {
    try {
      const response = await HorraireCommerceService.getHorraireCommerceById();
      dataToGetById.value = response.data;
      return response.data;
    } catch (error) {
      return Promise.reject(error as AxiosError);
    }
  };

  /**
   * Met à jour une devise spécifique sur le service distant.
   *
   * Cette fonction effectue une requête HTTP via `HorraireCommerceService`
   * pour modifier les informations d’une devise existante,
   * puis renvoie les données mises à jour.
   *
   * @async
   * @function updateHorraireCommerce
   * @param {Partial<IHorraireCommerceBody>} updateData - Données partielles de la devise à mettre à jour.
   * @returns {Promise<IHorraireCommerceDetail>} Promesse résolue contenant les données mises à jour de la devise.
   * @throws {AxiosError} Lève une erreur si la mise à jour échoue.
   */
  const updateHorraireCommerce = async (
    updateData: Partial<any>,
  ): Promise<INewScheduleItem> => {
    try {
      const response =
        await HorraireCommerceService.updateHorraireCommerce(updateData);
      return response.data;
    } catch (error) {
      return Promise.reject(error as AxiosError);
    }
  };

  /**
   * Supprime une devise spécifique via le service distant.
   *
   * Cette fonction envoie une requête HTTP à l’aide de `HorraireCommerceService`
   * pour retirer une devise existante,
   * puis renvoie la réponse du serveur en cas de succès.
   *
   * @async
   * @function deleteHorraireCommerce
   * @returns {Promise<any>} Promesse résolue contenant la réponse du serveur après suppression.
   * @throws {AxiosError} Lève une erreur si la suppression échoue.
   */
  const deleteHorraireCommerce = async (): Promise<any> => {
    try {
      const response = await HorraireCommerceService.deleteHorraireCommerce();
      return response;
    } catch (error) {
      return Promise.reject(error as AxiosError);
    }
  };

  const updateWeekdaySchedule = async (
    storeId: string,
    data: IWeekdayScheduleBody,
  ): Promise<any> => {
    try {
      const response = await HorraireCommerceService.updateWeekdaySchedule(
        storeId,
        data,
      );
      return response;
    } catch (error) {
      return Promise.reject(error as AxiosError);
    }
  };

  return {
    createHorraireCommerce,
    getHorraireCommerce,
    dataToGet,
    getHorraireCommerceById,
    dataToGetById,
    updateHorraireCommerce,
    updateWeekdaySchedule,
    deleteHorraireCommerce,
  };
});
